import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Add as AddIcon,
  School as CourseIcon,
  EmojiEvents as BadgeIcon,
  WorkspacePremium as CertificateIcon,
  TrendingUp as PointsIcon,
  Security as PermissionIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

interface User {
  id: string;
  keycloakUserId: string;
  displayName: string;
  email?: string;
  roles?: string[];
  totalPoints: number;
  currentStreak: number;
  createdAt: string;
  isActive?: boolean;
  currentSkillLevel?: { name: string; slug: string };
  preferredWorkArea?: { name: string; slug: string };
  _count: {
    enrollments: number;
    userBadges: number;
    issuedCertificates: number;
  };
}

interface UserDetail extends User {
  enrollments: Array<{
    id: string;
    status: string;
    progress: number;
    course: { title: string; slug: string };
  }>;
  userBadges: Array<{
    id: string;
    earnedAt: string;
    badge: { name: string; slug: string };
  }>;
  issuedCertificates: Array<{
    id: string;
    issuedAt: string;
    certificate: { name: string; course: { title: string } };
  }>;
  pointTransactions: Array<{
    id: string;
    points: number;
    reason: string;
    createdAt: string;
  }>;
}

interface ModulePermission {
  id: string;
  role: 'admin' | 'user';
  grantedAt: string;
  module: {
    id: string;
    title: string;
    slug: string;
    course: {
      id: string;
      title: string;
      slug: string;
    };
  };
}

interface CourseWithModules {
  id: string;
  title: string;
  modules: Array<{
    id: string;
    title: string;
    sortOrder: number;
  }>;
}

interface SkillLevel {
  id: string;
  name: string;
  slug: string;
}

interface WorkArea {
  id: string;
  name: string;
  slug: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Form state for editing
  const [editSkillLevel, setEditSkillLevel] = useState('');
  const [editWorkArea, setEditWorkArea] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editIsContentManager, setEditIsContentManager] = useState(false);

  // State for permission management
  const [addPermissionDialogOpen, setAddPermissionDialogOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [selectedPermissionRole, setSelectedPermissionRole] = useState<'admin' | 'user'>('user');
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<string>>(new Set());

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'users', page, rowsPerPage, search],
    queryFn: async () => {
      const response = await api.get('/admin/users', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined,
        },
      });
      return response.data;
    },
  });

  const { data: skillLevels = [] } = useQuery<SkillLevel[]>({
    queryKey: ['admin', 'skill-levels'],
    queryFn: async () => {
      const response = await api.get('/admin/skill-levels');
      return response.data;
    },
  });

  const { data: workAreas = [] } = useQuery<WorkArea[]>({
    queryKey: ['admin', 'work-areas'],
    queryFn: async () => {
      const response = await api.get('/admin/work-areas');
      return response.data;
    },
  });

  const { data: userDetail, isLoading: userDetailLoading } = useQuery<UserDetail>({
    queryKey: ['admin', 'user', selectedUser?.id],
    queryFn: async () => {
      const response = await api.get(`/admin/users/${selectedUser?.id}`);
      return response.data;
    },
    enabled: !!selectedUser?.id && dialogOpen,
  });

  // Fetch user module permissions
  const { data: userPermissions, isLoading: permissionsLoading } = useQuery<{ user: any; permissions: ModulePermission[] }>({
    queryKey: ['admin', 'user-permissions', selectedUser?.id],
    queryFn: async () => {
      const response = await api.get(`/admin/users/${selectedUser?.id}/module-permissions`);
      return response.data;
    },
    enabled: !!selectedUser?.id && dialogOpen && tabValue === 4,
  });

  // Fetch courses with modules for adding permissions
  const { data: coursesWithModules = [] } = useQuery<CourseWithModules[]>({
    queryKey: ['admin', 'courses-with-modules'],
    queryFn: async () => {
      const response = await api.get('/admin/courses/with-modules');
      return response.data;
    },
    enabled: dialogOpen && tabValue === 4,
  });

  // Add permission mutation
  const addUserPermissionMutation = useMutation({
    mutationFn: async (data: { moduleId: string; role: string }) => {
      const response = await api.post(`/admin/modules/${data.moduleId}/permissions`, {
        userId: selectedUser?.id,
        role: data.role,
      });
      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar('Berechtigung hinzugefuegt', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-permissions', selectedUser?.id] });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Hinzufuegen', { variant: 'error' });
    },
  });

  // Update permission mutation
  const updateUserPermissionMutation = useMutation({
    mutationFn: async (data: { moduleId: string; role: string }) => {
      const response = await api.patch(`/admin/modules/${data.moduleId}/permissions/${selectedUser?.id}`, {
        role: data.role,
      });
      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar('Berechtigung aktualisiert', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-permissions', selectedUser?.id] });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Aktualisieren', { variant: 'error' });
    },
  });

  // Delete permission mutation
  const deleteUserPermissionMutation = useMutation({
    mutationFn: async (moduleId: string) => {
      const response = await api.delete(`/admin/modules/${moduleId}/permissions/${selectedUser?.id}`);
      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar('Berechtigung entfernt', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-permissions', selectedUser?.id] });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Entfernen', { variant: 'error' });
    },
  });

  // Bulk update permissions mutation
  const bulkUpdatePermissionsMutation = useMutation({
    mutationFn: async (permissions: Array<{ moduleId: string; role: string }>) => {
      const response = await api.post(`/admin/users/${selectedUser?.id}/module-permissions/bulk`, {
        permissions,
      });
      return response.data;
    },
    onSuccess: (data) => {
      enqueueSnackbar(data.message || 'Berechtigungen aktualisiert', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-permissions', selectedUser?.id] });
      setSelectedPermissionIds(new Set());
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler bei Bulk-Aktion', { variant: 'error' });
    },
  });

  // Bulk delete selected permissions
  const handleBulkDelete = () => {
    if (!userPermissions?.permissions) return;
    const remainingPermissions = userPermissions.permissions
      .filter((p) => !selectedPermissionIds.has(p.module.id))
      .map((p) => ({ moduleId: p.module.id, role: p.role }));
    bulkUpdatePermissionsMutation.mutate(remainingPermissions);
  };

  // Bulk change role for selected permissions
  const handleBulkRoleChange = (newRole: 'admin' | 'user') => {
    if (!userPermissions?.permissions) return;
    const updatedPermissions = userPermissions.permissions.map((p) => ({
      moduleId: p.module.id,
      role: selectedPermissionIds.has(p.module.id) ? newRole : p.role,
    }));
    bulkUpdatePermissionsMutation.mutate(updatedPermissions);
  };

  // Toggle permission selection
  const togglePermissionSelection = (moduleId: string) => {
    const newSelection = new Set(selectedPermissionIds);
    if (newSelection.has(moduleId)) {
      newSelection.delete(moduleId);
    } else {
      newSelection.add(moduleId);
    }
    setSelectedPermissionIds(newSelection);
  };

  // Select/deselect all permissions
  const toggleSelectAll = () => {
    if (!userPermissions?.permissions) return;
    if (selectedPermissionIds.size === userPermissions.permissions.length) {
      setSelectedPermissionIds(new Set());
    } else {
      setSelectedPermissionIds(new Set(userPermissions.permissions.map((p) => p.module.id)));
    }
  };

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async (data: {
      userId: string;
      email?: string;
      displayName?: string;
      skillLevelSlug?: string;
      workAreaSlug?: string;
      roles?: string[];
    }) => {
      const response = await api.patch(`/admin/users/${data.userId}`, data);
      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar('Benutzer erfolgreich aktualisiert', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      handleCloseEditDialog();
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.message || 'Fehler beim Aktualisieren', { variant: 'error' });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      enqueueSnackbar('Benutzer erfolgreich gelöscht', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      handleCloseDeleteDialog();
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.message || 'Fehler beim Löschen', { variant: 'error' });
    },
  });

  const handleViewUser = (user: User) => {
    setSelectedUser(user as UserDetail);
    setDialogOpen(true);
    setTabValue(0);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user as UserDetail);
    setEditDisplayName(user.displayName || '');
    setEditEmail(user.email || '');
    setEditSkillLevel(user.currentSkillLevel?.slug || '');
    setEditWorkArea(user.preferredWorkArea?.slug || '');
    // Load roles from user data
    const userRoles = user.roles || [];
    setEditIsAdmin(userRoles.includes('admin'));
    setEditIsContentManager(userRoles.includes('content-manager'));
    setEditDialogOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user as UserDetail);
    setDeleteDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleUpdateUser = () => {
    if (!selectedUser) return;

    const roles: string[] = [];
    if (editIsAdmin) roles.push('admin');
    if (editIsContentManager) roles.push('content-manager');

    updateUserMutation.mutate({
      userId: selectedUser.id,
      email: editEmail || undefined,
      displayName: editDisplayName || undefined,
      skillLevelSlug: editSkillLevel || undefined,
      workAreaSlug: editWorkArea || undefined,
      roles,
    });
  };

  const handleConfirmDelete = () => {
    if (selectedUser) {
      deleteUserMutation.mutate(selectedUser.id);
    }
  };

  const users = usersData?.users || [];
  const totalUsers = usersData?.pagination?.total || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">
            {t('admin.users')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Benutzer werden automatisch beim ersten Login über Keycloak erstellt
          </Typography>
        </Box>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Benutzer suchen (Name, E-Mail)..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Benutzer</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>Skill-Level</TableCell>
                <TableCell align="center">Punkte</TableCell>
                <TableCell align="center">Kurse</TableCell>
                <TableCell>Registriert</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Keine Benutzer gefunden
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user: User) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                          {user.displayName?.charAt(0) || 'U'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {user.displayName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.currentSkillLevel?.name || 'Nicht gesetzt'}
                        size="small"
                        color={user.currentSkillLevel ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={500}>{user.totalPoints}</Typography>
                    </TableCell>
                    <TableCell align="center">{user._count.enrollments}</TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString('de-DE')}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Details anzeigen">
                        <IconButton size="small" onClick={() => handleViewUser(user)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => handleEditUser(user)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton size="small" onClick={() => handleDeleteUser(user)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Pro Seite:"
        />
      </Paper>

      {/* User Detail Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {selectedUser?.displayName?.charAt(0) || 'U'}
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedUser?.displayName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedUser?.email}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {userDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : userDetail ? (
            <>
              {/* Stats Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <PointsIcon color="primary" />
                      <Typography variant="h6">{userDetail.totalPoints}</Typography>
                      <Typography variant="caption">Punkte</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <CourseIcon color="success" />
                      <Typography variant="h6">{userDetail._count.enrollments}</Typography>
                      <Typography variant="caption">Kurse</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <BadgeIcon color="warning" />
                      <Typography variant="h6">{userDetail._count.userBadges}</Typography>
                      <Typography variant="caption">Badges</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1 }}>
                      <CertificateIcon color="info" />
                      <Typography variant="h6">{userDetail._count.issuedCertificates}</Typography>
                      <Typography variant="caption">Zertifikate</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Tabs */}
              <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
                <Tab label="Kurse" icon={<CourseIcon />} iconPosition="start" />
                <Tab label="Badges" icon={<BadgeIcon />} iconPosition="start" />
                <Tab label="Zertifikate" icon={<CertificateIcon />} iconPosition="start" />
                <Tab label="Punkte" icon={<PointsIcon />} iconPosition="start" />
                <Tab label="Berechtigungen" icon={<PermissionIcon />} iconPosition="start" />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <List>
                  {userDetail.enrollments?.map((enrollment) => (
                    <ListItem key={enrollment.id} divider>
                      <ListItemIcon>
                        <CourseIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={enrollment.course.title}
                        secondary={`Status: ${enrollment.status} | Fortschritt: ${enrollment.progress}%`}
                      />
                    </ListItem>
                  ))}
                  {(!userDetail.enrollments || userDetail.enrollments.length === 0) && (
                    <ListItem>
                      <ListItemText primary="Keine Kurse eingeschrieben" />
                    </ListItem>
                  )}
                </List>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <List>
                  {userDetail.userBadges?.map((ub) => (
                    <ListItem key={ub.id} divider>
                      <ListItemIcon>
                        <BadgeIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={ub.badge.name}
                        secondary={`Verdient am: ${new Date(ub.earnedAt).toLocaleDateString('de-DE')}`}
                      />
                    </ListItem>
                  ))}
                  {(!userDetail.userBadges || userDetail.userBadges.length === 0) && (
                    <ListItem>
                      <ListItemText primary="Keine Badges verdient" />
                    </ListItem>
                  )}
                </List>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <List>
                  {userDetail.issuedCertificates?.map((ic) => (
                    <ListItem key={ic.id} divider>
                      <ListItemIcon>
                        <CertificateIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary={ic.certificate.name}
                        secondary={`${ic.certificate.course.title} | ${new Date(ic.issuedAt).toLocaleDateString('de-DE')}`}
                      />
                    </ListItem>
                  ))}
                  {(!userDetail.issuedCertificates || userDetail.issuedCertificates.length === 0) && (
                    <ListItem>
                      <ListItemText primary="Keine Zertifikate ausgestellt" />
                    </ListItem>
                  )}
                </List>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <List>
                  {userDetail.pointTransactions?.slice(0, 10).map((pt) => (
                    <ListItem key={pt.id} divider>
                      <ListItemText
                        primary={pt.reason}
                        secondary={new Date(pt.createdAt).toLocaleDateString('de-DE')}
                      />
                      <Chip
                        label={`+${pt.points}`}
                        color="success"
                        size="small"
                      />
                    </ListItem>
                  ))}
                  {(!userDetail.pointTransactions || userDetail.pointTransactions.length === 0) && (
                    <ListItem>
                      <ListItemText primary="Keine Punktetransaktionen" />
                    </ListItem>
                  )}
                </List>
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  {/* Bulk actions - show when items selected */}
                  {selectedPermissionIds.size > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedPermissionIds.size} ausgewaehlt
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleBulkDelete}
                        disabled={bulkUpdatePermissionsMutation.isPending}
                      >
                        Entfernen
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AdminIcon />}
                        onClick={() => handleBulkRoleChange('admin')}
                        disabled={bulkUpdatePermissionsMutation.isPending}
                      >
                        Zu Admin
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PersonIcon />}
                        onClick={() => handleBulkRoleChange('user')}
                        disabled={bulkUpdatePermissionsMutation.isPending}
                      >
                        Zu Benutzer
                      </Button>
                    </Box>
                  )}
                  <Box sx={{ ml: 'auto' }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setAddPermissionDialogOpen(true)}
                      size="small"
                    >
                      Berechtigung hinzufuegen
                    </Button>
                  </Box>
                </Box>

                {permissionsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Select all checkbox */}
                    {userPermissions?.permissions && userPermissions.permissions.length > 0 && (
                      <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPermissionIds.size === userPermissions.permissions.length}
                              indeterminate={selectedPermissionIds.size > 0 && selectedPermissionIds.size < userPermissions.permissions.length}
                              onChange={toggleSelectAll}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Alle auswaehlen ({userPermissions.permissions.length})
                            </Typography>
                          }
                        />
                      </Box>
                    )}
                    <List>
                      {userPermissions?.permissions?.map((perm) => (
                        <ListItem
                          key={perm.id}
                          divider
                          sx={{
                            bgcolor: selectedPermissionIds.has(perm.module.id) ? 'action.selected' : 'inherit',
                          }}
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Rolle wechseln">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    updateUserPermissionMutation.mutate({
                                      moduleId: perm.module.id,
                                      role: perm.role === 'admin' ? 'user' : 'admin',
                                    });
                                  }}
                                >
                                  {perm.role === 'admin' ? <PersonIcon /> : <AdminIcon />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Entfernen">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => deleteUserPermissionMutation.mutate(perm.module.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                        >
                          <Checkbox
                            checked={selectedPermissionIds.has(perm.module.id)}
                            onChange={() => togglePermissionSelection(perm.module.id)}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <ListItemIcon>
                            <PermissionIcon color={perm.role === 'admin' ? 'error' : 'primary'} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {perm.module.title}
                                <Chip
                                  icon={perm.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                                  label={perm.role === 'admin' ? 'Admin' : 'Benutzer'}
                                  color={perm.role === 'admin' ? 'error' : 'primary'}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            }
                            secondary={`${perm.module.course.title} | Vergeben am: ${new Date(perm.grantedAt).toLocaleDateString('de-DE')}`}
                          />
                        </ListItem>
                      ))}
                      {(!userPermissions?.permissions || userPermissions.permissions.length === 0) && (
                        <ListItem>
                          <ListItemText
                            primary="Keine Modul-Berechtigungen"
                            secondary="Klicken Sie auf 'Berechtigung hinzufuegen' um diesem Benutzer Zugang zu Modulen zu geben."
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}
              </TabPanel>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Benutzer bearbeiten: {selectedUser?.displayName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="E-Mail"
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
            />

            <TextField
              fullWidth
              label="Anzeigename"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel>Skill-Level</InputLabel>
              <Select
                value={editSkillLevel}
                label="Skill-Level"
                onChange={(e) => setEditSkillLevel(e.target.value)}
              >
                <MenuItem value="">Nicht gesetzt</MenuItem>
                {skillLevels.map((sl) => (
                  <MenuItem key={sl.id} value={sl.slug}>
                    {sl.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Arbeitsbereich</InputLabel>
              <Select
                value={editWorkArea}
                label="Arbeitsbereich"
                onChange={(e) => setEditWorkArea(e.target.value)}
              >
                <MenuItem value="">Nicht gesetzt</MenuItem>
                {workAreas.map((wa) => (
                  <MenuItem key={wa.id} value={wa.slug}>
                    {wa.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Rollen
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editIsAdmin}
                    onChange={(e) => setEditIsAdmin(e.target.checked)}
                  />
                }
                label="Administrator"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editIsContentManager}
                    onChange={(e) => setEditIsContentManager(e.target.checked)}
                  />
                }
                label="Content Manager"
              />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleUpdateUser}
            disabled={updateUserMutation.isPending}
          >
            {updateUserMutation.isPending ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Benutzer löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den Benutzer <strong>{selectedUser?.displayName}</strong> wirklich löschen?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten des Benutzers
            (Kurse, Badges, Zertifikate, Punkte) werden gelöscht.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? <CircularProgress size={20} /> : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Permission Dialog */}
      <Dialog
        open={addPermissionDialogOpen}
        onClose={() => {
          setAddPermissionDialogOpen(false);
          setSelectedModuleId('');
          setSelectedPermissionRole('user');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Berechtigung hinzufuegen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Fuer: <strong>{selectedUser?.displayName}</strong>
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Modul auswaehlen</InputLabel>
              <Select
                value={selectedModuleId}
                label="Modul auswaehlen"
                onChange={(e) => setSelectedModuleId(e.target.value)}
              >
                {coursesWithModules.map((course) => [
                  <MenuItem key={`course-${course.id}`} disabled sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                    {course.title}
                  </MenuItem>,
                  ...course.modules
                    .filter((m) => !userPermissions?.permissions?.some((p) => p.module.id === m.id))
                    .map((module) => (
                      <MenuItem key={module.id} value={module.id} sx={{ pl: 4 }}>
                        {module.title}
                      </MenuItem>
                    )),
                ])}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Rolle</InputLabel>
              <Select
                value={selectedPermissionRole}
                label="Rolle"
                onChange={(e) => setSelectedPermissionRole(e.target.value as 'admin' | 'user')}
              >
                <MenuItem value="user">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    Benutzer - Kann das Modul nutzen
                  </Box>
                </MenuItem>
                <MenuItem value="admin">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminIcon fontSize="small" />
                    Admin - Kann das Modul verwalten
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddPermissionDialogOpen(false);
              setSelectedModuleId('');
              setSelectedPermissionRole('user');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!selectedModuleId || addUserPermissionMutation.isPending}
            onClick={() => {
              addUserPermissionMutation.mutate({
                moduleId: selectedModuleId,
                role: selectedPermissionRole,
              });
              setAddPermissionDialogOpen(false);
              setSelectedModuleId('');
              setSelectedPermissionRole('user');
            }}
          >
            {addUserPermissionMutation.isPending ? <CircularProgress size={20} /> : 'Hinzufuegen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
